import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  filter,
  map,
  switchMap,
  takeUntil,
  throwError,
} from 'rxjs';
import { HelixBookRepositoryService } from '../../services/helix-book-repository.service';
import { BreadcrumbItem } from '@shared/modules/breadcrumb/types/breadcrumbItem';
import { KbGeneticDictionaryItemResponse } from '@medindex-webapi';
import { MetaService } from '@shared/services/meta/meta.service';
import { JsonLdStorage } from '@shared/services/meta/jsonld-schema-storage';

@Component({
  selector: 'app-dict',
  templateUrl: './dict.component.html',
  styleUrls: ['./dict.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HelixBookRepositoryService],
})
export class DictComponent implements OnInit, OnDestroy {
  constructor(
    private route: ActivatedRoute,
    private repository: HelixBookRepositoryService,
    private metaService: MetaService
  ) {}

  ngUnsubscribe$ = new Subject<void>();
  isLoaded$ = new BehaviorSubject(false);
  showError$ = new BehaviorSubject(false);
  item$ = new BehaviorSubject<KbGeneticDictionaryItemResponse | null>(null);
  breadcrumbs$: Observable<BreadcrumbItem[]> = this.item$.pipe(
    filter((item): item is KbGeneticDictionaryItemResponse => !!item),
    map((item) => {
      return [
        { href: '/', text: 'Главная' },
        { href: '/kb', text: 'Helixbook' },
        { href: '', text: item.title ?? '' },
      ];
    })
  );

  ngOnInit(): void {
    this.route.params
      .pipe(
        switchMap((params) => {
          const title = (params.id as string) || '';

          if (!title) return throwError(() => new Error('KbItemNotFound'));

          return this.repository.getGeneticDictionaryItem(title);
        }),
        takeUntil(this.ngUnsubscribe$),
        catchError((err) => {
          this.isLoaded$.next(true);
          this.showError$.next(true);
          return throwError(() => err);
        })
      )
      .subscribe((item) => {
        this.item$.next(item);
        this.isLoaded$.next(true);
        this.metaService.title(item.title ?? item.metaInfo?.title ?? '');
        this.metaService.description(item.metaInfo?.description ?? '');
        this.metaService.keywords(item.metaInfo?.keywords ?? '');
        const jsonLd = JsonLdStorage.Article(item);
        this.metaService.insertJsonLdSchema(jsonLd);
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
